import React, { memo, useMemo } from 'react';
import {
  useLocation,
  match,
  Link,
  useRouteMatch,
  matchPath
} from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemText,
  IconButton,
  ListItemIcon
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import TIRouteProps from 'modules/common/routes/TIRouteProps';
import { useFeatureFlag } from 'modules/common/hooks';
import { useDefaultRoutes } from './useDefaultRoutes';

type PageStyleProps = {
  selected: boolean;
};

const useListStyles = makeStyles((theme) => ({
  root: {
    marginLeft: theme.spacing(-0.5)
  }
}));

const useListItemStyles = makeStyles((theme) => ({
  root: {
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)'
    },
    '&:focus': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)'
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.text.primary
    },
    color: theme.palette.grey[400],
    outline: 0
  }
}));

const useStyles = makeStyles((theme) => ({
  text: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    fontSize: theme.typography.body2.fontSize,
    fontFamily: theme.typography.h6.fontFamily,
    fontWeight: theme.typography.fontWeightMedium,
    color: ({ selected }: PageStyleProps) =>
      selected ? 'inherit' : theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
    ...theme.typography.body1
  },
  icon: {
    color: ({ selected }: PageStyleProps) =>
      selected ? 'inherit' : theme.palette.text.secondary,
    '&:hover': {
      background: 'none'
    }
  },
  listIcon: {
    color: ({ selected }: PageStyleProps) =>
      selected ? 'inherit' : theme.palette.text.secondary,
    '&:hover': {
      background: 'none'
    },
    minWidth: theme.spacing(0),
    padding: theme.spacing(1)
  }
}));

export const buildLinkPath = (
  pathMatch: match,
  dynamicPath?: string
): string | undefined => {
  const pathEndsWithSlash = pathMatch.path.endsWith('/');
  const urlEndsWithSlash = pathMatch.url.endsWith('/');

  return dynamicPath?.replace(
    pathMatch.path,
    `${pathMatch.url}${pathEndsWithSlash && !urlEndsWithSlash ? '/' : ''}`
  );
};

type SidePanelOptionProps = {
  route: TIRouteProps;
  selected: boolean;
  path?: string;
};

export const SidePanelOption: React.FC<SidePanelOptionProps> = ({
  route,
  selected,
  path
}: SidePanelOptionProps) => {
  const routeMatch = useRouteMatch();
  const listItemClasses = useListItemStyles();
  const classes = useStyles({ selected });
  const primaryTypographyProps = useMemo(() => ({ className: classes.text }), [
    classes.text
  ]);
  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');

  return (
    <ListItem
      classes={listItemClasses}
      component={Link}
      to={route.link ? route.link : buildLinkPath(routeMatch, path) ?? '/'}
      selected={selected}
      dense={false}
      data-testid={path}
    >
      {isTiwScreenReaderEnabled ? (
        <ListItemIcon className={classes.listIcon} aria-hidden="true">
          {route.icon}
        </ListItemIcon>
      ) : (
        <IconButton className={classes.icon} disableRipple>
          {route.icon}
        </IconButton>
      )}
      <ListItemText
        primary={route.title}
        primaryTypographyProps={primaryTypographyProps}
      />
    </ListItem>
  );
};

export const SidePanelOptions: React.FC = memo(() => {
  const location = useLocation();
  const routes = useDefaultRoutes();
  const classes = useListStyles();
  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');

  return (
    <nav>
      <List disablePadding classes={classes}>
        {Object.values(routes).map((route, index) => {
          const path = Array.isArray(route.path) ? route.path[0] : route.path;
          const selected = !!matchPath(location.pathname, route);
          return isTiwScreenReaderEnabled ? (
            <li key={index}>
              <SidePanelOption
                route={route}
                path={path}
                selected={selected}
                key={index}
              />
            </li>
          ) : (
            <SidePanelOption
              route={route}
              path={path}
              selected={selected}
              key={index}
            />
          );
        })}
      </List>
    </nav>
  );
});

SidePanelOptions.displayName = 'SidePanelOptions';
